import { useEffect, useRef, useState } from "react";
import { ChevronDown, Upload, X } from "lucide-react";
import { v4 as uuid } from "uuid";
import { getPreSignedUrlForUpload } from "../../../api/admin";
import {
  PARAMETER_ICONS,
  UPLOADED_ICON_PREFIX,
  findIconDefinition,
  isUploadedIcon,
  UploadedParameterIcon,
} from "../../../utils/parameterIcons";

const MAX_ICON_SIZE_BYTES = 1024 * 1024; // 1MB

interface IconSelectorProps {
  value?: string;
  onChange: (icon: string) => void;
}

export function IconSelector({ value, onChange }: IconSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const selectedDef = findIconDefinition(value);
  const hasUploadedIcon = isUploadedIcon(value);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    if (!file.type.startsWith("image/")) {
      setUploadError("Please select an image file.");
      return;
    }
    if (file.size > MAX_ICON_SIZE_BYTES) {
      setUploadError("Icon image must be smaller than 1MB.");
      return;
    }

    setIsUploading(true);
    try {
      const key = `${UPLOADED_ICON_PREFIX}${uuid()}/${file.name}`;
      const res = await getPreSignedUrlForUpload({
        contentType: file.type,
        key,
      });
      const uploadRes = await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("Upload failed");
      onChange(res.key);
      setIsOpen(false);
    } catch (err) {
      console.error("Error uploading icon:", err);
      setUploadError("Failed to upload icon. Please try again.");
    } finally {
      setIsUploading(false);
      if (inputFileRef.current) inputFileRef.current.value = "";
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputFileRef}
        className="hidden"
        type="file"
        accept="image/*"
        onChange={handleFileChange}
      />

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base flex items-center justify-between"
      >
        <span className="flex items-center gap-3 min-w-0">
          {hasUploadedIcon && value ? (
            <>
              <UploadedParameterIcon iconKey={value} className="w-5 h-5" />
              <span className="truncate">Custom icon</span>
            </>
          ) : selectedDef ? (
            <>
              <selectedDef.Icon className="w-5 h-5" />
              <span className="truncate">{selectedDef.label}</span>
            </>
          ) : (
            <span className="text-gray-400">Select an icon</span>
          )}
        </span>
        <span className="flex items-center gap-1">
          {(selectedDef || hasUploadedIcon) && (
            <span
              role="button"
              aria-label="Clear icon"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </span>
          )}
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </span>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3">
          <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
            {PARAMETER_ICONS.map((def) => (
              <button
                key={def.name}
                type="button"
                title={def.label}
                onClick={() => {
                  onChange(def.name);
                  setIsOpen(false);
                }}
                className={`flex items-center justify-center p-2.5 rounded-md border transition-colors ${
                  value === def.name
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                    : "border-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                }`}
              >
                <def.Icon className="w-5 h-5" />
              </button>
            ))}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 mt-3 pt-3">
            <button
              type="button"
              disabled={isUploading}
              onClick={() => inputFileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {isUploading ? "Uploading..." : "Upload custom icon"}
            </button>
            {uploadError && (
              <p className="text-red-500 text-xs mt-2">{uploadError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
